import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import trackingApi from '../services/trackingApi';
import unifiedApi from '../services/unifiedApi';
import gpsWebSocketService from '../services/gpsWebSocketService';
import logger from '@/utils/logger';
import { POLLING, PAGINATION } from '../config/constants';
import { getSchoolId, setSchoolId, getToken } from '@/utils/storage';

// ─── Pure helper ─────────────────────────────────────────────────────────────

// Même pattern que trParents.js/Layout.jsx — pas de helper JWT partagé dans ce
// projet, dupliqué par convention plutôt que factorisé pour une seule ligne.
const _parseJwt = (token) => {
  try {
    // JWT payload est en base64url (RFC 4648 §5, '-'/'_' au lieu de '+'/'/') —
    // atob() n'accepte que l'alphabet base64 standard et lève sinon.
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return JSON.parse(new TextDecoder('utf-8').decode(bytes));
  } catch {
    return null;
  }
};

export function stopBelongsToRoute(stop, routeId) {
  if (!stop || routeId == null) return false;
  if (
    String(stop.route_id) === String(routeId) ||
    String(stop.routeId) === String(routeId) ||
    String(stop.circuit_id) === String(routeId)
  )
    return true;
  const links = stop.routes || stop.route_stops;
  if (!Array.isArray(links)) return false;
  return links.some((link) => {
    const rid = link.route_id ?? link.route?.id ?? link.routes?.id;
    return String(rid) === String(routeId);
  });
}

const OFFLINE_THRESHOLD_MS = 15 * 60 * 1000;

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useGpsSuivi = () => {
  const navigate = useNavigate();

  // Bouton "Terminer le trajet" réservé au superadmin en impersonation —
  // jamais visible pour un admin école normal.
  const isImpersonating = !!_parseJwt(getToken())?.impersonated_by;

  const [gpsData, setGpsData] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [stops, setStops] = useState([]);
  const [buses, setBuses] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [schoolInfo, setSchoolInfo] = useState(null);

  const [wsConnected, setWsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [routeFilter, setRouteFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');

  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(POLLING.GPS_INTERVAL_MS);
  const [showRoutes, setShowRoutes] = useState(true);
  const [mapRouteStopsDirection, setMapRouteStopsDirection] = useState('inbound');
  const [loadingCircuits, setLoadingCircuits] = useState(false);
  const [circuits, setCircuits] = useState([]);
  const [backendCircuits, setBackendCircuits] = useState([]);
  const [showStops, setShowStops] = useState(true);

  const [expandedRows, setExpandedRows] = useState({});
  const [tripDetails, setTripDetails] = useState({});
  const [loadingTripDetails, setLoadingTripDetails] = useState({});
  const [activeTrips, setActiveTrips] = useState([]);
  const [activeMenu, setActiveMenu] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);

  // ─── Click outside ──────────────────────────────────────────────────────────

  useEffect(() => {
    const handler = () => setActiveMenu(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // ─── Derived ────────────────────────────────────────────────────────────────

  const center = useMemo(() => {
    if (schoolInfo?.latitude && schoolInfo?.longitude) {
      return { lat: parseFloat(schoolInfo.latitude), lng: parseFloat(schoolInfo.longitude) };
    }
    return { lat: 33.5731, lng: -7.5898 };
  }, [schoolInfo?.latitude, schoolInfo?.longitude]);

  // ─── WebSocket callbacks ─────────────────────────────────────────────────────

  const refreshBackendCircuitsFromApi = useCallback(async () => {
    try {
      const res = await unifiedApi.tracking.getCircuits();
      if (res?.success && Array.isArray(res.data)) setBackendCircuits(res.data);
    } catch (err) {
      logger.warn('⚠️ [GpsSuivi] Rafraîchissement circuits impossible', err);
    }
  }, []);

  const handleTripStarted = useCallback(
    (tripData) => {
      if (tripData.tripType === 'service') return;
      const tid = tripData.tripId != null ? String(tripData.tripId) : null;
      const rid = tripData.routeId != null ? String(tripData.routeId) : null;

      setActiveTrips((prev) => {
        const exists = tid && prev.find((t) => String(t.id) === tid);
        if (exists) {
          return prev.map((t) =>
            String(t.id) === tid
              ? { ...t, status: 'active', ...tripData, id: tid || t.id, route_id: rid || t.route_id }
              : t,
          );
        }
        return [
          ...prev,
          {
            id: tid,
            status: 'active',
            route_id: rid,
            bus_id: tripData.busId,
            driver_id: tripData.driverId,
            start_time: tripData.startTime,
            completedStops: 0,
            completed_stops: 0,
            route: { name: tripData.routeName },
          },
        ];
      });

      setBackendCircuits((prev) =>
        prev.map((circuit) =>
          rid && String(circuit.id) === rid
            ? {
                ...circuit,
                status: 'En route',
                activeBus: true,
                tripId: tid,
                progress: 0,
                studentsOnboard: 0,
                completedStops: 0,
              }
            : circuit,
        ),
      );

      void refreshBackendCircuitsFromApi();
    },
    [refreshBackendCircuitsFromApi],
  );

  const handleTripEnded = useCallback((data) => {
    toast.success(`Le trajet du bus ${data.busNumber || 'Inconnu'} est terminé`, {
      description: `Circuit: ${data.routeName || 'Non spécifié'}`,
      duration: 5000,
    });
    setActiveTrips((prev) =>
      prev.map((t) =>
        t.id === data.tripId ? { ...t, status: 'completed', end_time: new Date().toISOString(), progress: 100 } : t,
      ),
    );
    setBackendCircuits((prev) =>
      prev.map((circuit) =>
        String(circuit.tripId) === String(data.tripId)
          ? { ...circuit, status: 'Terminé', activeBus: false, progress: 100 }
          : circuit,
      ),
    );
  }, []);

  // Bouton superadmin (impersonation uniquement) — force la clôture d'un trip
  // resté ouvert. 3e chemin isolé côté backend, voir docs/ideas/force-trip-ended.md.
  // Ne réutilise pas handleTripEnded (évite un double toast si le WS trip_ended
  // arrive aussi juste après) — met à jour l'état local directement.
  const handleForceEndTrip = useCallback(async (tripId, busNumber) => {
    if (!tripId) return;

    const confirmed = window.confirm(
      `Forcer la fin du trajet du bus ${busNumber || 'inconnu'} ?\n\nCette action est irréversible et ne doit être utilisée que pour débloquer un trajet resté ouvert par erreur.`,
    );
    if (!confirmed) return;

    try {
      const response = await unifiedApi.trips.forceEnd(tripId);
      if (response.success) {
        toast.success('Trajet clôturé avec succès');
        setActiveTrips((prev) =>
          prev.map((t) =>
            t.id === tripId ? { ...t, status: 'completed', end_time: new Date().toISOString(), progress: 100 } : t,
          ),
        );
        setBackendCircuits((prev) =>
          prev.map((circuit) =>
            String(circuit.tripId) === String(tripId)
              ? { ...circuit, status: 'Terminé', activeBus: false, progress: 100 }
              : circuit,
          ),
        );
      } else {
        toast.error(response.message || 'Impossible de terminer le trajet');
      }
    } catch (err) {
      logger.error('Erreur force-end trip:', err);
      toast.error('Erreur lors de la clôture du trajet');
    }
  }, []);

  const handleStudentScanned = useCallback((data) => {
    setTripDetails((prevDetails) => {
      const tripId = data.tripId;
      const currentDetails = prevDetails[tripId];
      if (!currentDetails) {
        setActiveTrips((prevTrips) =>
          prevTrips.map((trip) => {
            if (trip.id === tripId) {
              const cnt = trip.current_students || trip.picked_up_count || 0;
              return {
                ...trip,
                current_students: cnt + 1,
                picked_up_count: cnt + 1,
                last_update: new Date().toISOString(),
              };
            }
            return trip;
          }),
        );
        return prevDetails;
      }

      const updatedStudents = (currentDetails.students || []).map((s) =>
        String(s.id) === String(data.studentId) ? { ...s, status: data.status, pickup_time: data.pickupTime } : s,
      );

      const updatedStops = (currentDetails.stops || []).map((stop) => {
        const studentInStop = (stop.students || []).find((s) => String(s.id) === String(data.studentId));
        if (!studentInStop) return stop;
        const updatedStopStudents = (stop.students || []).map((s) =>
          String(s.id) === String(data.studentId) ? { ...s, status: data.status, pickup_time: data.pickupTime } : s,
        );
        const presentCount = updatedStopStudents.filter((s) => s.status === 'present' || s.status === 'boarded').length;
        const allPresent = presentCount === updatedStopStudents.length && updatedStopStudents.length > 0;
        return {
          ...stop,
          students: updatedStopStudents,
          studentsPresent: presentCount,
          pickedUpCount: presentCount,
          status: allPresent ? 'visited' : presentCount > 0 ? 'active' : stop.status,
          isCompleted: allPresent || stop.isCompleted,
        };
      });

      const totalOnboard = updatedStops.reduce((acc, s) => acc + (s.studentsPresent || 0), 0);
      const totalStopsCount = updatedStops.length;
      const completedCount = updatedStops.filter((s) => s.isCompleted || s.status === 'visited').length;
      const newProgress = totalStopsCount > 0 ? Math.round((completedCount / totalStopsCount) * 100) : 0;

      setActiveTrips((prevTrips) =>
        prevTrips.map((trip) =>
          trip.id === tripId
            ? {
                ...trip,
                studentsOnboard: totalOnboard,
                current_students: totalOnboard,
                picked_up_count: totalOnboard,
                progress: newProgress,
                last_update: new Date().toISOString(),
              }
            : trip,
        ),
      );
      setBackendCircuits((prevCircuits) =>
        prevCircuits.map((circuit) =>
          String(circuit.tripId) === String(tripId)
            ? { ...circuit, studentsOnboard: totalOnboard, progress: newProgress }
            : circuit,
        ),
      );

      return {
        ...prevDetails,
        [tripId]: {
          ...currentDetails,
          students: updatedStudents,
          stops: updatedStops,
          studentsOnboard: totalOnboard,
          progress: newProgress,
        },
      };
    });
  }, []);

  // Reflet temps réel du tap parent (parentTripConfirmationService.js) — ne
  // touche jamais `status`/pickup_time/compteurs (contrairement à
  // handleStudentScanned ci-dessus) : signal purement observationnel, ne
  // doit jamais être confondu avec un pointage GPS/chauffeur réel (Règle n°2
  // CLAUDE.md). Un seul événement peut porter plusieurs studentIds (fratrie
  // confirmée en un tap côté parent).
  const handleParentAttendanceConfirmed = useCallback((data) => {
    const tripId = data.tripId;
    const studentIds = (data.studentIds || []).map(String);
    if (!tripId || studentIds.length === 0) return;

    setTripDetails((prevDetails) => {
      const currentDetails = prevDetails[tripId];
      if (!currentDetails) return prevDetails;

      const patchStudent = (s) =>
        studentIds.includes(String(s.id)) ? { ...s, parentConfirmedAt: data.confirmedAt } : s;

      return {
        ...prevDetails,
        [tripId]: {
          ...currentDetails,
          students: (currentDetails.students || []).map(patchStudent),
          stops: (currentDetails.stops || []).map((stop) => ({
            ...stop,
            students: (stop.students || []).map(patchStudent),
          })),
        },
      };
    });
  }, []);

  const handleTripUpdate = useCallback(
    (data) => {
      const tripIdRaw = data.tripId ?? data.trip_id;
      if (tripIdRaw == null || tripIdRaw === '') return;
      const tripKey = String(tripIdRaw);

      setActiveTrips((prev) =>
        prev.map((trip) => {
          if (String(trip.id) !== tripKey) return trip;
          const next = { ...trip, last_update: new Date().toISOString() };
          if (data.progress != null) next.progress = data.progress;
          if (data.completedStops != null) {
            next.completedStops = data.completedStops;
            next.completed_stops = data.completedStops;
            next.studentsOnboard = data.completedStops;
          }
          if (data.totalStops != null) {
            next.total_stops = data.totalStops;
            next.totalStops = data.totalStops;
          }
          if (data.status) next.status = data.status;
          return next;
        }),
      );

      setBackendCircuits((prev) =>
        prev.map((circuit) => {
          if (String(circuit.tripId) !== tripKey) return circuit;
          const next = { ...circuit };
          if (data.progress != null) next.progress = data.progress;
          if (data.completedStops != null) {
            next.completedStops = data.completedStops;
            next.studentsOnboard = data.completedStops;
          }
          if (data.totalStops != null) next.totalStops = data.totalStops;
          if (data.status) next.status = data.status === 'completed' ? 'Terminé' : 'En route';
          return next;
        }),
      );

      setTripDetails((prev) => {
        const currentDetails = prev[tripKey] || {};
        const merged = { ...currentDetails };
        if (data.progress != null) merged.progress = data.progress;
        if (data.completedStops != null) {
          merged.completedStops = data.completedStops;
          merged.studentsOnboard = data.completedStops;
        }
        if (data.totalStops != null) merged.totalStops = data.totalStops;
        if (data.status) merged.status = data.status;
        merged.isRealTime = true;
        return { ...prev, [tripKey]: merged };
      });

      if (data.presentStudents && Array.isArray(data.presentStudents)) {
        data.presentStudents.forEach((student) =>
          handleStudentScanned({
            tripId: tripKey,
            studentId: student.student_id,
            status: student.status,
            pickupTime: student.updated_at,
          }),
        );
      }
      if (data.completedStops != null) void refreshBackendCircuitsFromApi();
    },
    [handleStudentScanned, refreshBackendCircuitsFromApi],
  );

  const handleStopCompleted = useCallback((data) => {
    setTripDetails((prevDetails) => {
      const tripId = String(data.tripId ?? data.trip_id ?? '');
      if (!tripId) return prevDetails;
      const currentDetails = prevDetails[tripId];
      if (!currentDetails) {
        setActiveTrips((prevTrips) =>
          prevTrips.map((trip) =>
            String(trip.id) === tripId
              ? { ...trip, last_stop_completed: data.stopId, last_update: new Date().toISOString() }
              : trip,
          ),
        );
        return prevDetails;
      }

      const updatedStops = (currentDetails.stops || []).map((stop) => {
        const match =
          String(stop.id) === String(data.stopId) ||
          String(stop.stopId) === String(data.stopId) ||
          String(stop.routeStopId) === String(data.stopId);
        if (match)
          return {
            ...stop,
            isCompleted: true,
            status: 'visited',
            completedAt: data.arrivedAt || new Date().toISOString(),
          };
        return stop;
      });

      setActiveTrips((prevTrips) =>
        prevTrips.map((trip) =>
          String(trip.id) === tripId
            ? { ...trip, last_stop_completed: data.stopId, last_update: new Date().toISOString() }
            : trip,
        ),
      );
      setBackendCircuits((prevCircuits) =>
        prevCircuits.map((circuit) =>
          String(circuit.tripId) === tripId ? { ...circuit, lastStopCompleted: data.stopId } : circuit,
        ),
      );

      return { ...prevDetails, [tripId]: { ...currentDetails, stops: updatedStops } };
    });
  }, []);

  const updateGPSData = useCallback(
    (newData) => {
      const patch = { ...newData };
      const eventSchoolId = patch.schoolId ?? patch.school_id;
      if (eventSchoolId != null && schoolInfo?.id != null && String(eventSchoolId) !== String(schoolInfo.id)) return;

      setGpsData((prev) => {
        const busId = patch.bus_id || patch.busId;
        if (busId) {
          const idx = prev.findIndex((item) => item.bus_id === busId || item.busId === busId);
          if (idx >= 0) {
            const arr = [...prev];
            arr[idx] = { ...arr[idx], ...patch };
            return arr;
          }
        }
        const idxById = prev.findIndex((item) => item.id === patch.id);
        if (idxById >= 0) {
          const arr = [...prev];
          arr[idxById] = { ...arr[idxById], ...patch };
          return arr;
        }
        return [...prev, patch];
      });
      setLastUpdate(new Date());
    },
    [schoolInfo?.id],
  );

  const setupWebSocket = useCallback(() => {
    try {
      const storedSchoolId = getSchoolId();
      const currentSchoolId = schoolInfo?.id || storedSchoolId;
      if (currentSchoolId) gpsWebSocketService.connect(currentSchoolId);
      else gpsWebSocketService.connect();

      gpsWebSocketService.subscribeToGPSUpdates(updateGPSData);

      if (gpsWebSocketService.socket) {
        gpsWebSocketService.socket.on('trip_started', handleTripStarted);
        gpsWebSocketService.socket.on('trip_ended', handleTripEnded);
        gpsWebSocketService.socket.on('stop_completed', handleStopCompleted);
        gpsWebSocketService.socket.on('trip_update', handleTripUpdate);
        gpsWebSocketService.socket.on('student_scanned', handleStudentScanned);
        gpsWebSocketService.socket.on('parent_attendance_confirmed', handleParentAttendanceConfirmed);
      }

      gpsWebSocketService.subscribeToConnectionStatus((status) => {
        if (status.connected) {
          setWsConnected(true);
          setConnectionStatus('connected');
        } else {
          setWsConnected(false);
          setConnectionStatus(status.reason || 'disconnected');
        }
      });
    } catch (err) {
      logger.error('❌ Erreur WebSocket:', err);
      setWsConnected(false);
    }
  }, [
    handleTripStarted,
    handleTripEnded,
    handleStopCompleted,
    handleStudentScanned,
    handleParentAttendanceConfirmed,
    handleTripUpdate,
    schoolInfo,
    updateGPSData,
  ]);

  // ─── Effects ────────────────────────────────────────────────────────────────

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [busLocations, schoolData, routesData, stopsData, busesData, driversData, activeTripsData, circuitsData] =
        await Promise.all([
          trackingApi.getAllBusLocations(),
          unifiedApi.schools.getCurrent(),
          unifiedApi.routes.getAll({ status: 'active' }),
          (unifiedApi.stops.getAllUnlimited
            ? unifiedApi.stops.getAllUnlimited()
            : unifiedApi.stops.getAll({ limit: PAGINATION.STOPS_LIMIT })
          ).catch(() => unifiedApi.stops.getAll({ limit: PAGINATION.STOPS_LIMIT })),
          unifiedApi.buses.getAll(),
          unifiedApi.drivers.getAll(),
          unifiedApi
            .get('/admin/tracking/trips', {
              params: { status: 'in_progress,completed', limit: PAGINATION.TRIPS_LIMIT },
            })
            .catch(() => ({ success: false, data: [] })),
          unifiedApi.tracking.getCircuits().catch(() => ({ success: false, data: [] })),
        ]);

      if (circuitsData?.success) setBackendCircuits(circuitsData.data);

      const trips = activeTripsData?.success
        ? activeTripsData.data
        : Array.isArray(activeTripsData)
          ? activeTripsData
          : [];
      const relevantTrips = trips.filter((t) => ['active', 'in_progress', 'completed'].includes(t.status));
      const mappedTrips = relevantTrips.map((t) => ({
        ...t,
        completedStops: t.completedStops !== undefined ? t.completedStops : t.completed_stops || 0,
        totalStops: t.totalStops !== undefined ? t.totalStops : t.total_stops || 0,
        progress: t.progress !== undefined ? t.progress : 0,
        stopsCompleted: t.completedStops !== undefined ? t.completedStops : t.completed_stops || 0,
        stopsTotal: t.totalStops !== undefined ? t.totalStops : t.total_stops || 0,
        studentsOnboard: t.student_count || 0,
        studentsTotal: t.students_total || 0,
      }));
      setActiveTrips(mappedTrips);

      setGpsData(busLocations || []);

      const rawRoutes = routesData?.data || [];
      const rawStops = stopsData?.data || [];
      const enrichedRoutes = rawRoutes.map((route) => {
        if (route.stops && route.stops.length > 0) return route;
        const routeStops = rawStops.filter((s) => stopBelongsToRoute(s, route.id));
        return routeStops.length > 0 ? { ...route, stops: routeStops } : route;
      });

      setRoutes(enrichedRoutes);
      setStops(rawStops);
      setBuses(busesData?.data || []);
      setDrivers(driversData?.data || []);
      if (schoolData?.data) {
        const sd = schoolData.data;
        setSchoolInfo({
          id: sd.id,
          name: sd.name,
          latitude: sd.latitude,
          longitude: sd.longitude,
          address: sd.address,
          city: sd.city,
          country: sd.country,
        });
        setSchoolId(sd.id);
      } else {
        setSchoolInfo({
          name: 'École Al Massira',
          latitude: 33.5731,
          longitude: -7.5898,
          address: 'Casablanca, Maroc',
        });
      }

      setLastUpdate(new Date());
    } catch (err) {
      logger.error('❌ Erreur chargement données GPS:', err);
      setError('Erreur lors du chargement des données GPS');
      setGpsData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
    return () => {
      gpsWebSocketService.disconnect();
    };
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      const id = setInterval(() => setLastUpdate(new Date()), refreshInterval);
      return () => clearInterval(id);
    }
  }, [autoRefresh, refreshInterval]);

  useEffect(() => {
    if (showRoutes && circuits.length === 0 && !loadingCircuits) loadCircuits();
  }, [showRoutes]);

  useEffect(() => {
    if (schoolInfo?.id) setupWebSocket();
  }, [schoolInfo?.id, setupWebSocket]);

  useEffect(() => {
    const onIdle = (data) => {
      if (data) updateGPSData({ ...data, bus_id: data.busId ?? data.bus_id });
    };
    const onOffline = (data) => {
      if (data?.busId) updateGPSData({ bus_id: data.busId, busId: data.busId, derivedStatus: 'offline' });
    };
    gpsWebSocketService.on('bus_idle_update', onIdle);
    gpsWebSocketService.on('bus_offline', onOffline);
    return () => {
      gpsWebSocketService.off('bus_idle_update', onIdle);
      gpsWebSocketService.off('bus_offline', onOffline);
    };
  }, [updateGPSData]);

  // ─── Action handlers ─────────────────────────────────────────────────────────

  const loadCircuits = async () => {
    try {
      setLoadingCircuits(true);
      const response = await unifiedApi.routes.getVisualization();
      if (response.success) setCircuits(Array.isArray(response.data) ? response.data : []);
      else logger.warn('⚠️ Erreur chargement circuits viz:', response.message);
    } catch (err) {
      logger.error('❌ Erreur chargement circuits viz:', err);
    } finally {
      setLoadingCircuits(false);
    }
  };

  const refreshGPSData = async () => {
    try {
      setLastUpdate(new Date());
    } catch (err) {
      logger.error('Erreur rafraîchissement GPS:', err);
    }
  };

  const toggleRow = async (tripId, routeId) => {
    const id = tripId != null && tripId !== '' ? String(tripId) : `route-${routeId}`;
    const wasExpanded = !!expandedRows[id];
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));

    const opening = !wasExpanded;
    const existing = tripDetails[id];
    const hasStopsList = Array.isArray(existing?.stops) && existing.stops.length > 0;
    const canFetch = tripId != null && tripId !== '' && !String(tripId).startsWith('trip-');

    if (opening && canFetch && !hasStopsList) {
      setLoadingTripDetails((prev) => ({ ...prev, [id]: true }));
      try {
        const details = await unifiedApi.tracking.getTripAttendance(String(tripId));
        if (details) {
          setTripDetails((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...details, isRealTime: true } }));
        }
      } catch (err) {
        logger.error('Erreur chargement détails trajet:', err);
      } finally {
        setLoadingTripDetails((prev) => ({ ...prev, [id]: false }));
      }
    }
  };

  // ─── Computed ────────────────────────────────────────────────────────────────

  const circuitsDisplay = useMemo(() => {
    const activeBusesMap = new Map();
    gpsData.forEach((bus) => {
      if (bus.route_id || bus.assigned_route_id) activeBusesMap.set(bus.route_id || bus.assigned_route_id, bus);
    });

    const stopsArray = Array.isArray(stops) ? stops : stops?.data || [];
    const routeStopsForId = (routeId) => {
      let rs = stopsArray.filter((s) => stopBelongsToRoute(s, routeId));
      const routeMeta = routes.find((r) => String(r.id) === String(routeId));
      if (rs.length === 0 && routeMeta?.stops?.length) rs = routeMeta.stops;
      return rs;
    };
    const nextStopFromVisitIndex = (routeId, visitIndex) => {
      const routeStops = routeStopsForId(routeId);
      if (!routeStops.length) return null;
      const n = Math.max(0, Math.floor(Number(visitIndex) || 0));
      if (n >= routeStops.length) return 'Garage';
      const s = routeStops[n];
      return s?.stop_name || s?.name || (n === 0 ? 'Premier arrêt' : 'Arrêt suivant');
    };

    const derivedRows = routes.map((route) => {
      const backendRow = backendCircuits.find((bc) => String(bc.id) === String(route.id));
      const stopsTotalFromBackend = Math.max(
        Number(backendRow?.studentsTotal) || 0,
        Number(backendRow?.totalStops) || 0,
      );

      const routeTrips = activeTrips.filter((t) => {
        const rid = t.route_id || t.routeId || t.circuit_id || t.routes?.id;
        return String(rid) === String(route.id);
      });
      let activeTrip = routeTrips.find((t) => t.status === 'active' || t.status === 'in_progress');
      if (!activeTrip && routeTrips.length > 0) {
        routeTrips.sort((a, b) => new Date(b.start_time || 0) - new Date(a.start_time || 0));
        activeTrip = routeTrips[0];
      }

      let activeBus = activeBusesMap.get(route.id);
      if (!activeBus && activeTrip && (activeTrip.bus_id || activeTrip.busId)) {
        const busId = activeTrip.bus_id || activeTrip.busId;
        activeBus = gpsData.find((b) => String(b.bus_id || b.id || b.busId || '') === String(busId));
      }

      const routeBusId = route.bus_id ?? route.assigned_bus_id;
      let assignedBus = buses.find(
        (b) =>
          String(b.circuitId) === String(route.id) ||
          String(b.circuit_id) === String(route.id) ||
          String(b.assigned_route_id) === String(route.id),
      );
      if (!assignedBus && routeBusId) assignedBus = buses.find((b) => String(b.id) === String(routeBusId));
      if (!activeBus && assignedBus) {
        const aid = String(assignedBus.id).toLowerCase();
        activeBus = gpsData.find((b) => {
          const gid = b.id || b.bus_id || b.busId;
          return gid && String(gid).toLowerCase() === aid;
        });
      }

      const routeDriverId = route.driver_id || route.default_driver_id || route.assigned_driver_id;
      const assignedDriver = routeDriverId ? drivers.find((d) => String(d.id) === String(routeDriverId)) : null;

      const pickJoined = (rel) => (rel == null ? null : Array.isArray(rel) ? rel[0] : rel);
      const routeBusJoined = pickJoined(route.buses ?? route.bus);
      const routeDriverJoined = pickJoined(route.drivers ?? route.driver);
      const busFromRouteList = routeBusId ? buses.find((b) => String(b.id) === String(routeBusId)) : null;

      const busLabelFromRoute =
        routeBusJoined?.bus_number ||
        routeBusJoined?.license_plate ||
        busFromRouteList?.bus_number ||
        busFromRouteList?.license_plate ||
        busFromRouteList?.plate_number ||
        '';

      const driverLabelFromRoute = (() => {
        if (routeDriverJoined) {
          const n = [
            routeDriverJoined.first_name ?? routeDriverJoined.firstName,
            routeDriverJoined.last_name ?? routeDriverJoined.lastName,
          ]
            .filter(Boolean)
            .join(' ')
            .trim();
          if (n) return n;
        }
        if (assignedDriver) {
          if (assignedDriver.firstName && assignedDriver.lastName)
            return `${assignedDriver.firstName} ${assignedDriver.lastName}`;
          return (
            `${assignedDriver.first_name ?? ''} ${assignedDriver.last_name ?? ''}`.trim() ||
            assignedDriver.name ||
            assignedDriver.full_name ||
            ''
          );
        }
        return '';
      })();
      const driverPhoneFromRoute = routeDriverJoined?.phone ?? assignedDriver?.phone ?? null;

      const isTripLive = !!activeTrip && ['active', 'in_progress'].includes(activeTrip.status);
      const tripId =
        activeTrip?.id != null
          ? String(activeTrip.id)
          : activeBus?.trip_id != null
            ? String(activeBus.trip_id)
            : activeBus
              ? `trip-${activeBus.id}`
              : null;

      const gpsBusLabel =
        activeBus && (activeBus.bus_number || activeBus.license_plate || activeBus.licensePlate || '');
      let driverName =
        driverLabelFromRoute ||
        (activeBus?.driver_name && isTripLive ? activeBus.driver_name : null) ||
        route.default_driver?.name ||
        'Non assigné';
      let busNumber =
        isTripLive && gpsBusLabel
          ? gpsBusLabel
          : busLabelFromRoute ||
            (assignedBus
              ? assignedBus.bus_number || assignedBus.license_plate || 'Bus sans numéro'
              : route.default_bus?.number) ||
            'Non assigné';
      let driverPhone =
        isTripLive && activeBus?.driver_phone
          ? activeBus.driver_phone
          : driverPhoneFromRoute || route.default_driver?.phone || null;

      const routeStopsArr = routeStopsForId(route.id);
      const routeStopsCount = routeStopsArr.length;
      let progress = 0;
      let nextStop = 'N/A';
      let stopsVisited = 0;
      const realtimeDetails = tripDetails[tripId];

      if (realtimeDetails) {
        if (realtimeDetails.progress !== undefined) progress = realtimeDetails.progress;
        if (realtimeDetails.completedStops !== undefined) stopsVisited = realtimeDetails.completedStops;
        else if (Array.isArray(realtimeDetails.stops))
          stopsVisited = realtimeDetails.stops.filter((s) => s.isCompleted || s.status === 'visited').length;
        else if (realtimeDetails.completed_stops !== undefined) stopsVisited = realtimeDetails.completed_stops;
      } else if (activeTrip) {
        if (activeTrip.progress !== undefined) progress = activeTrip.progress;
        stopsVisited = activeTrip.completedStops ?? activeTrip.stopsCompleted ?? activeTrip.completed_stops ?? 0;
      }

      const stopsTotalForUi = Math.max(
        routeStopsCount,
        stopsTotalFromBackend,
        realtimeDetails?.stops?.length || 0,
        Number(realtimeDetails?.totalStops) || 0,
        Number(activeTrip?.total_stops ?? activeTrip?.totalStops) || 0,
      );
      const visitedForProgress = Math.max(
        stopsVisited,
        Number(backendRow?.completedStops) || 0,
        Number(backendRow?.studentsOnboard) || 0,
      );
      let progressOut = progress || 0;
      if (stopsTotalForUi > 0 && visitedForProgress > 0)
        progressOut = Math.max(progressOut, Math.min(100, Math.round((visitedForProgress / stopsTotalForUi) * 100)));
      if (stopsTotalForUi > 0 && visitedForProgress >= stopsTotalForUi) {
        progressOut = 100;
        if (!nextStop || nextStop === 'N/A') nextStop = 'Garage';
      }

      if ((!nextStop || nextStop === 'N/A') && backendRow?.nextStop && backendRow.nextStop !== 'N/A')
        nextStop = backendRow.nextStop;

      if (isTripLive && routeStopsArr.length > 0) {
        const done = Math.max(
          Number(stopsVisited) || 0,
          Number(activeTrip?.completed_stops ?? activeTrip?.completedStops ?? 0) || 0,
          Number(realtimeDetails?.completedStops ?? realtimeDetails?.completed_stops ?? 0) || 0,
          Number(backendRow?.completedStops) || 0,
          Number(backendRow?.studentsOnboard) || 0,
        );
        if (done >= routeStopsArr.length) nextStop = 'Garage';
        else {
          const ns = routeStopsArr[done];
          const nm = ns?.stop_name || ns?.name;
          if (nm) nextStop = nm;
        }
      }

      const delay = activeBus?.delay_minutes || 0;
      const status = (() => {
        if (activeTrip) {
          if (['active', 'in_progress', 'Active', 'In Progress'].includes(activeTrip.status)) {
            if (activeBus) {
              const ds = activeBus.derivedStatus;
              if (ds === 'parked' || ds === 'parked_mid_trip') return 'Garé';
              if (ds === 'active_moving') return 'En route';
              if (ds === 'active_stopped') return 'Arrêté';
              return (activeBus.speed || 0) > 10 ? 'En route' : 'Arrêté';
            }
            return 'Signal perdu';
          }
          if (activeTrip.status === 'completed') return 'Terminé';
          if (activeTrip.status === 'scheduled') return 'Prêt';
          return 'En route';
        }
        if (activeBus) return 'En route';
        if (tripDetails[tripId] && tripDetails[tripId].status !== 'completed') return 'En route';
        return 'Inactif';
      })();

      return {
        ...route,
        name: route.route_name || route.name || route.route_code || 'Circuit',
        activeBus,
        tripId,
        progress: progressOut,
        nextStop: nextStop || 'N/A',
        delay: delay || 0,
        studentsOnboard: stopsVisited || 0,
        studentsTotal: stopsTotalForUi,
        code: route.route_code || route.code || 'Sans code',
        status,
        driverName,
        driverPhone,
        busNumber,
        lastUpdate: activeBus?.last_update,
        speed: activeBus?.speed || 0,
      };
    });

    const derivedByRouteId = new Map(derivedRows.map((row) => [String(row.id), row]));

    if (backendCircuits.length === 0) return derivedRows;

    const isTripStatusLive = (s) => {
      if (s == null || s === '') return false;
      const x = String(s).toLowerCase();
      return x === 'active' || x === 'in_progress';
    };

    return backendCircuits.map((bc) => {
      const liveTrip = activeTrips.find((t) => {
        const rid = t.route_id || t.routeId || t.circuit_id || t.routes?.id;
        return String(rid) === String(bc.id) && isTripStatusLive(t.status);
      });

      const tripDetailsVisit = (tidStr) => {
        if (tidStr == null || tidStr === '') return 0;
        const td = tripDetails[String(tidStr)];
        return Number(td?.completedStops ?? td?.completed_stops ?? td?.studentsOnboard ?? 0) || 0;
      };

      if (!liveTrip) {
        const tid = bc.tripId != null ? String(bc.tripId) : null;
        const liveByCircuit = tid && activeTrips.find((t) => String(t.id) === tid && isTripStatusLive(t.status));
        if ((bc.status === 'En route' || bc.activeBus) && (liveByCircuit || tid)) {
          const mergedVisit = Math.max(
            Number(bc.completedStops) || 0,
            Number(bc.studentsOnboard) || 0,
            Number(liveByCircuit?.completedStops ?? liveByCircuit?.completed_stops ?? 0) || 0,
            tripDetailsVisit(tid),
          );
          const nxt =
            nextStopFromVisitIndex(bc.id, mergedVisit) || (bc.nextStop && bc.nextStop !== 'N/A' ? bc.nextStop : null);
          return { ...bc, studentsOnboard: mergedVisit, nextStop: nxt || bc.nextStop || 'N/A' };
        }
        return bc;
      }

      const derived = derivedByRouteId.get(String(bc.id));
      if (!derived) {
        const mv = Math.max(
          Number(liveTrip.completedStops ?? liveTrip.completed_stops ?? liveTrip.studentsOnboard ?? 0) || 0,
          Number(bc.completedStops) || 0,
          Number(bc.studentsOnboard) || 0,
          tripDetailsVisit(liveTrip.id),
        );
        const nxt = nextStopFromVisitIndex(bc.id, mv) || (bc.nextStop && bc.nextStop !== 'N/A' ? bc.nextStop : null);
        return {
          ...bc,
          tripId: liveTrip.id,
          status: 'En route',
          activeBus: true,
          progress: liveTrip.progress ?? bc.progress ?? 0,
          studentsOnboard: mv,
          nextStop: nxt || 'N/A',
          studentsTotal: liveTrip.studentsTotal ?? liveTrip.total_stops ?? bc.studentsTotal,
        };
      }

      const mv = Math.max(
        derived.studentsOnboard || 0,
        Number(bc.completedStops) || 0,
        Number(bc.studentsOnboard) || 0,
        Number(liveTrip.completedStops ?? liveTrip.completed_stops ?? liveTrip.stopsCompleted ?? 0) || 0,
        tripDetailsVisit(liveTrip.id),
      );
      const nxt =
        nextStopFromVisitIndex(bc.id, mv) ||
        (derived.nextStop && derived.nextStop !== 'N/A' ? derived.nextStop : null) ||
        (bc.nextStop && bc.nextStop !== 'N/A' ? bc.nextStop : null);
      return {
        ...bc,
        tripId: derived.tripId ?? liveTrip.id,
        nextStop: nxt || 'N/A',
        progress: Math.max(Number(derived.progress) || 0, Number(bc.progress) || 0),
        studentsOnboard: mv,
        studentsTotal: Math.max(derived.studentsTotal || 0, Number(bc.studentsTotal) || 0, Number(bc.totalStops) || 0),
        status: derived.status,
        activeBus: derived.activeBus ?? bc.activeBus,
        delay: derived.delay,
        driverName: derived.driverName,
        busNumber: derived.busNumber,
        driverPhone: derived.driverPhone,
        speed: derived.speed,
        lastUpdate: derived.lastUpdate,
      };
    });
  }, [routes, stops, gpsData, buses, drivers, tripDetails, activeTrips, backendCircuits]);

  const filteredCircuits = useMemo(() => {
    return circuitsDisplay.filter((circuit) => {
      const matchesSearch =
        (circuit.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (circuit.driverName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (circuit.busNumber || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && circuit.activeBus) ||
        (statusFilter === 'inactive' && !circuit.activeBus);
      return matchesSearch && matchesStatus;
    });
  }, [circuitsDisplay, searchTerm, statusFilter]);

  const allVehiclesForMap = useMemo(() => {
    const gpsVehicles = gpsData.map((v) => {
      let status = 'active';
      if (v.ignition === false) {
        status = 'parked';
      } else {
        const age = v.timestamp ? Date.now() - new Date(v.timestamp).getTime() : Infinity;
        if (age > OFFLINE_THRESHOLD_MS) status = 'offline';
        else {
          const at = activeTrips.find((t) => t.bus_id === v.bus_id || t.busId === v.bus_id);
          if (at && v.speed <= 2) status = 'stopped';
        }
      }
      return { ...v, hasGps: true, status, ignition: v.ignition ?? null, buffered: v.buffered ?? false };
    });

    const busesWithGps = new Set();
    gpsData.forEach((v) => {
      const vBusId = String(v.bus_id || v.busId || '').toLowerCase();
      if (vBusId && vBusId !== 'undefined' && vBusId !== 'null') busesWithGps.add(vBusId);
      else {
        const vId = String(v.id || '').toLowerCase();
        if (vId && vId !== 'undefined' && vId !== 'null') busesWithGps.add(vId);
      }
      const vPlate = String(v.plate_number || v.plateNumber || v.bus_number || '').toLowerCase();
      if (vPlate && vPlate !== 'undefined') {
        const matchingBus = buses.find(
          (b) =>
            String(b.plate_number || '').toLowerCase() === vPlate ||
            String(b.bus_number || '').toLowerCase() === vPlate,
        );
        if (matchingBus) busesWithGps.add(String(matchingBus.id).toLowerCase());
      }
    });

    const staticBuses = buses
      .filter((b) => !busesWithGps.has(String(b.id).toLowerCase()))
      .map((b) => {
        const at = activeTrips.find((t) => t.bus_id === b.id || t.busId === b.id);
        return {
          id: b.id,
          bus_number: b.bus_number,
          latitude: schoolInfo?.latitude || 33.5731,
          longitude: schoolInfo?.longitude || -7.5898,
          status: at ? 'lost_signal' : 'offline',
          hasGps: false,
          driver_name: at ? 'Signal perdu' : 'Non connecté',
          speed: 0,
        };
      });

    return [...gpsVehicles, ...staticBuses];
  }, [gpsData, buses, schoolInfo, activeTrips]);

  return {
    gpsData,
    routes,
    stops,
    buses,
    drivers,
    selectedVehicle,
    setSelectedVehicle,
    loading,
    error,
    lastUpdate,
    schoolInfo,
    center,
    wsConnected,
    connectionStatus,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    routeFilter,
    setRouteFilter,
    sourceFilter,
    setSourceFilter,
    autoRefresh,
    setAutoRefresh,
    refreshInterval,
    setRefreshInterval,
    showRoutes,
    setShowRoutes,
    mapRouteStopsDirection,
    setMapRouteStopsDirection,
    loadingCircuits,
    circuits,
    showStops,
    setShowStops,
    backendCircuits,
    expandedRows,
    tripDetails,
    loadingTripDetails,
    activeTrips,
    activeMenu,
    setActiveMenu,
    selectedDriver,
    setSelectedDriver,
    circuitsDisplay,
    filteredCircuits,
    allVehiclesForMap,
    loadInitialData,
    refreshGPSData,
    loadCircuits,
    toggleRow,
    navigate,
    isImpersonating,
    handleForceEndTrip,
  };
};
